import * as _ from 'underscore';
import { CheckboxState } from '../../common/components/checkboxes/CheckboxState';
import { CommonStyles } from '../../common/react/CommonStyles';
import { IAuthenticationProviderSelectorModel } from '../directives/tbAuthenticationProviderSelector';
import { ISiteRolePickerMenuScope, SiteRolePickerMenuHelper } from '../helpers/SiteRolePickerMenuHelper';
import { L10n } from '../L10n/L10n';
import { IUserInfo } from '../models/IUserInfo';
import { Set } from '../models/Set';
import { SetOfUsers } from '../models/SetOfUsers';
import { Module } from '../Module';
import { ServerUsers } from '../resources/ServerUsers';
import { DefaultSiteRole, SiteRoleOption, SiteRoleOptionsResource } from '../resources/SiteRoleOptions';
import { SiteRolesResource } from '../resources/SiteRoles';
import { SiteUsers } from '../resources/SiteUsers';
import { Users } from '../resources/Users';
import { ICheckUserNamesResult, IUserNameAvailability } from '../ServerApi/requests/CheckUserNamesRequest';
import { IUpdateServerUsersSiteMembershipResult } from '../ServerApi/requests/UpdateServerUsersSiteMembershipRequest';
import { IResult } from '../ServerApi/types/IResponse';
import { IServerUser } from '../ServerApi/types/IServerUser';
import { SiteRoles } from '../ServerApi/types/ISiteRole';
import { SiteUserAuthSetting } from '../ServerApi/types/ISiteUser';
import { IUserActionResult, UserActionResultCode } from '../ServerApi/types/IUserActionResult';
import { AddUsersOptionsAction, UserAddedEvent } from '../services/AddUsersOptionsAction';
import { PasswordPolicyScope, PasswordPolicyService } from '../services/PasswordPolicyService';
import { ServerService } from '../services/ServerService';
import { AuthenticationProviderType, IAuthenticationProvider } from '../services/SiteAuthenticationSettingsService';
import { SiteRolesPickerClientService } from '../services/SiteRolesPickerClientService';
import {
  AddUserToSiteActionNotification,
  ServerAdminUpdateActionNotification,
  SiteMembershipUpdateActionNotification,
  UserActionNotification,
  UserActionNotificationParams,
} from '../services/UserActionNotification';
import { SiteRolesEvent } from './SiteMembershipDialogCtrl';
import { ISiteRoleChanges } from './SiteRolesPickerCtrl';

// Possible states that the form can get into.
// Aside from the initial state, the form has 3 other different flows
// depending on availability of the specified username.
export let AddLocalUserFormState = {
  Initial: 'initial',
  NewUser: 'newUser',
  OtherSite: 'otherSite',
  NotAvailable: 'notAvailable'
};

// Contains the model for the whole form, including all different flows.
export interface IFormModel {
  username: string;
  formState: string;
  errorText: string;
  newUserFlowModel: INewUserFlowModel;
  otherSiteFlowModel: IOtherSiteFlowModel;
  notAvailableFlowModel: INotAvailableFlowModel;
  authenticationProviderSelectorModel: IAuthenticationProviderSelectorModel;
}

// Model for ux Flow #1:
// => Username is available
export interface INewUserFlowModel {
  displayName: string;
  password: string;
  confirmPassword: string;
  email: string;
  usernameInfo: string;
}

// Model for ux Flow #2:
// => Username exists on another site
export interface IOtherSiteFlowModel {
  displayName: string;
  warningText: string;
  proceedClicked: boolean;
}

// Model for ux Flow #3:
// => Username exists on current site
export interface INotAvailableFlowModel {
  displayName: string;
  errorText: string;
}

// Scope interface used by the controller class.
export interface IAddLocalUserDialogScope extends ISiteRolePickerMenuScope {
  addLocalUserForm: ng.IFormController;
  busy: boolean;
  checkDoesUsernameExists: (username: string, authenticationProvider: IAuthenticationProvider) => void;
  close: (result: any) => void;
  confirm: () => void;
  confirmLabel: () => string;
  formModel: IFormModel;
  isConfirmDisabled: () => boolean;
  isServerMode: () => boolean;
  isSiteMode: () => boolean;
  mode: string;
  proceed(): void;
  proceedClicked(): boolean;
  passwordFieldsAreNonEmpty: () => boolean;
  siteRoleChanges: ISiteRoleChanges; // for server mode

  // for the site selector (siteRoleDropdown mixin)
  setSiteRole(role: string): void;
  siteRoleOptions: string[];
  siteRole: string;

  // quota
  availableQuota: number;
  isQuotaEnabledForSite: boolean;
  isSiteRoleOptionDisabled(siteRole: string): boolean;
  showAuthenticationProviderSelector: boolean;
  showPasswordFields: () => boolean;
  siteAuthDefaultLabel: () => string;
  submitButtonLabel: () => string;
  passwordPoliciesValidationCheck: () => boolean;
}

export class AddLocalUserDialogCtrl {

  public static $inject = [
    '$scope',
    '$q',
    '$translate',
    'AddUserToSiteActionNotification',
    'UserActionNotification',
    'mode',
    'ServerUsers',
    'ServerAdminUpdateActionNotification',
    'SiteMembershipUpdateActionNotification',
    'SiteRoles',
    'SiteRoleOptionsResource',
    'SiteRolesPickerClientService',
    'SiteUsers',
    'Users',
    'AddUsersOptionsAction',
    'availableQuota',
    'PasswordPolicyService',
    'ServerService'
  ];

  constructor(
    private $scope: IAddLocalUserDialogScope,
    private $q: ng.IQService,
    private $translate: ng.translate.ITranslateService,
    private addUserToSiteActionNotification: AddUserToSiteActionNotification,
    private userActionNotification: UserActionNotification,
            mode: string,
    private ServerUsers: ServerUsers,
    private serverAdminUpdateActionNotification: ServerAdminUpdateActionNotification,
    private siteMembershipUpdateActionNotification: SiteMembershipUpdateActionNotification,
            siteRoles: SiteRolesResource,
    private siteRolesOptionsResource: SiteRoleOptionsResource,
    private rolesPickerClient: SiteRolesPickerClientService,
    private SiteUsers: SiteUsers,
            Users: Users,
    private addUsersOptionsAction: AddUsersOptionsAction,

    // resolved in AddLocalUserAction
            availableQuota: number,
    private passwordPolicyService: PasswordPolicyService,
    private serverService: ServerService
  ) {
    // site or server
    $scope.mode = mode;

    $scope.isServerMode = () => {
      return $scope.mode === 'server';
    };
    $scope.isSiteMode = () => {
      return $scope.mode === 'site';
    };

    // setup site role stuff
    if ($scope.isServerMode()) {
      // site role changes will be stored here
      $scope.siteRoleChanges = {
        siteRoles: {},
        serverAdmin: undefined
      };
    } else if ($scope.isSiteMode()) {
      // possible site roles settings for the imported group include everything except guest & admin.
      $scope.siteRoleOptions = siteRoles.optionsForCurrentUser();
      $scope.siteRole = availableQuota <= 0 ? SiteRoles.Unlicensed : DefaultSiteRole;
      $scope.setSiteRole = (role: string) => {
        $scope.siteRole = role;
      };
    } else {
      throw new Error('Unknown mode, it is neither "site" or "server"');
    }

    // initial state of the form
    $scope.formModel = {
      username: '',
      formState: AddLocalUserFormState.Initial,
      errorText: '',
      newUserFlowModel: {
        displayName: '',
        password: '',
        confirmPassword: '',
        email: '',
        usernameInfo: ''
      },
      otherSiteFlowModel: {
        displayName: '',
        warningText: '',
        proceedClicked: false
      },
      notAvailableFlowModel: {
        displayName: '',
        errorText: ''
      },
      authenticationProviderSelectorModel: this.addUsersOptionsAction.getInitialAuthenticationProviderSelectorModel()
    };

    $scope.showAuthenticationProviderSelector =  $scope.isSiteMode() && this.serverService.isSiteSamlEnabled();
    $scope.$watch('formModel.authenticationProviderSelectorModel.authenticationProvider', (newVal: IAuthenticationProvider, oldVal: IAuthenticationProvider) => {
      if (newVal === oldVal) return;
      $scope.checkDoesUsernameExists($scope.formModel.username, newVal);
    });

    // proceed button on Flow #2 (username exist on another site)
    $scope.proceed = () => {
      $scope.siteRoleSelectorProps.isDisabled = false; //enable license tier site role dropdown now that proceed button is clicked
      this.getOtherSiteFlowModel().proceedClicked = true;
    };

    // returns true when the proceed button has been clicked on Flow #2.
    $scope.proceedClicked = () => {
      return this.getOtherSiteFlowModel().proceedClicked;
    };

    // called when "Add User" button gets clicked
    $scope.confirm = () => {
      this.confirm();
    };

    // determines whether the "Add User" button is disabled
    $scope.isConfirmDisabled = () => {
      return this.isConfirmDisabled();
    };

    // called everytime the username or authenticationProvider changes
    $scope.checkDoesUsernameExists = (username: string, authenticationProvider: IAuthenticationProvider) => {
      if (!username) {
         $scope.formModel.formState = AddLocalUserFormState.NewUser;
         $scope.formModel.newUserFlowModel.usernameInfo = '';
         return;
      }

      Users.checkUserNames([username]).then((results: ICheckUserNamesResult) => {
        if (results.users.length === 0) {
          $scope.formModel.formState = AddLocalUserFormState.Initial;
          return;
        }

        const result: IUserNameAvailability = results.users[0];

        // $note-ltan:
        // We can not assume we are dealing with site users even when existsOnSite is true.
        // Thus the explicity mode check.
        if ($scope.isSiteMode() && result.existsOnSite) {
          this.setupFormForNotAvailable(result.displayName, 'usernameExistsOnCurrentSite');
        } else if (result.existsOnServer) {
          if ($scope.isSiteMode()) {
            if ((result.userAuthSetting === SiteUserAuthSetting.Default) && (authenticationProvider.type === AuthenticationProviderType.default)) {
              this.setupFormForAddUserToSite(result.displayName);
            } else {
              this.setupFormForNotAvailable(result.displayName, 'userNameExistsOnServerForAddUserToSiteSAML');
            }
          } else {
            if (result.userAuthSetting === SiteUserAuthSetting.Default) {
              this.setupFormForNotAvailable(result.displayName, 'usernameExistsOnServer');
            } else {
              this.setupFormForNotAvailable(result.displayName, 'userNameExistsOnServerForAddUserToSiteSAML');
            }
          }
        } else {
          this.setupFormForAvailable();
        }
      });
    };

    const onSiteRoleSelected = (roleOption: SiteRoleOption) => {
      $scope.$apply(() => {
        $scope.siteRoleSelectorProps.selectedRole = roleOption;
        $scope.setSiteRole(roleOption.role);
      });
    };

    const shouldTierRoleDropdownBeInitiallyDisabled = (): boolean => {
      return $scope.isSiteMode() && $scope.formModel.formState === AddLocalUserFormState.OtherSite && !$scope.proceedClicked();
    };

    const DefaultLocalUserDropdownWidth = 300;

    SiteRolePickerMenuHelper.setupPickerMenuScope($scope, $translate, serverService, this.siteRolesOptionsResource, $scope.siteRole, onSiteRoleSelected, !serverService.isSessionServerAdmin());
    $scope.siteRoleSelectorProps.isDisabled = shouldTierRoleDropdownBeInitiallyDisabled();
    $scope.siteRoleSelectorProps.style = { width: DefaultLocalUserDropdownWidth - CommonStyles.Sizes.DefaultSpace - (2 * CommonStyles.Sizes.TextBoxBorderSize) };

    // true if the passwords & confirm password fields are both non-empty
    $scope.passwordFieldsAreNonEmpty = () => {
      const model = $scope.formModel.newUserFlowModel;
      return model.password &&
        model.confirmPassword &&
        model.password.length > 0 &&
        model.confirmPassword.length > 0;
    };

    // TODO: dry this out. Duplicated in AddActiveDirectorySiteUsersDialogCtrl
    $scope.availableQuota = availableQuota;

    SiteRolePickerMenuHelper.setScopeToDisableAllLicensedRolesIfOutOfQuota($scope);

    $scope.isSiteRoleOptionDisabled = (siteRole: string) => {
      // when available quota is 0, only allow Unlicensed and UnlicensedWithPublish users to be added.
      return availableQuota <= 0 && siteRole !== SiteRoles.Unlicensed && siteRole !== SiteRoles.UnlicensedWithPublish;
    };
    $scope.isQuotaEnabledForSite = _.isNumber(availableQuota);
    $scope.siteAuthDefaultLabel = () => serverService.siteAuthDefaultLabel();

    $scope.showPasswordFields = () => {
      const authenticationProvider = $scope.formModel.authenticationProviderSelectorModel.authenticationProvider;
      return authenticationProvider.type === AuthenticationProviderType.default && !serverService.isAuthenticationSaml();
    };
    $scope.submitButtonLabel = () => {
      if(!serverService.isTableauIdSupported() && this.$scope.proceedClicked()) {
        return this.$translate.instant('addUserToSite');
      }
      return this.$translate.instant('addUsersDialogSubmit');
    };

    $scope.$watch('formModel.username', (newVal: string, oldVal: string) => {
      if (newVal === oldVal) return;
      $scope.checkDoesUsernameExists(newVal, $scope.formModel.authenticationProviderSelectorModel.authenticationProvider);
    });

    $scope.passwordPoliciesValidationCheck =
        passwordPolicyService.loadPoliciesIntoScope(<any>$scope, () => $scope.formModel.newUserFlowModel.password, () => {});

  }

  private setupFormForAddUserToSite(displayName: string) {
    this.$scope.formModel.formState = AddLocalUserFormState.OtherSite;
    this.getOtherSiteFlowModel().displayName = displayName;
    this.getOtherSiteFlowModel().warningText = this.$translate.instant('usernameExistsOnAnotherSite', { USER_NAME: L10n.elideIfLong(displayName) });
    this.getOtherSiteFlowModel().proceedClicked = false;
    this.$scope.siteRoleSelectorProps.isDisabled = true; // disable tier licensing site role picker dropdown until "Proceed" button is clicked
  }

  private setupFormForNotAvailable(displayName: string, errorStringId: string) {
    this.$scope.formModel.formState = AddLocalUserFormState.NotAvailable;
    this.getNotAvailableFlowModel().displayName = displayName;
    this.getNotAvailableFlowModel().errorText = this.$translate.instant(errorStringId, { USER_NAME: L10n.elideIfLong(displayName) });
  }

  private setupFormForAvailable() {
    this.$scope.formModel.formState = AddLocalUserFormState.NewUser;
    this.getNewUserFlowModel().usernameInfo = this.$translate.instant('usernameAvailable');
    this.$scope.siteRoleSelectorProps.isDisabled = false; // enable tier licensing site role picker dropdown in case it was disabled before
  }

  // various logic that depends on the state of the form

  private confirm() {
    this.$scope.busy = true;
    const username = this.$scope.formModel.username;
    const authenticationProvider = this.$scope.formModel.authenticationProviderSelectorModel.authenticationProvider;
    switch (this.getFormState()) {

      case AddLocalUserFormState.NewUser:
        if (this.$scope.isSiteMode()) {
          this.createLocalSiteUser(username, this.getNewUserFlowModel(), authenticationProvider);
        } else if (this.$scope.isServerMode()) {
          this.createLocalServerUser(username, this.getNewUserFlowModel(), authenticationProvider);
        }
        break;

      case AddLocalUserFormState.OtherSite:
        this.addUserToSite(username, this.getOtherSiteFlowModel());
        break;
    }
  }

  private isConfirmDisabled(): boolean {
    if (this.$scope.busy) {
      return true;
    }

    if (this.$scope.addLocalUserForm.$invalid) {
      return true;
    }

    if (!this.$scope.passwordPoliciesValidationCheck()) {
      return true;
    }

    switch (this.getFormState()) {

      case AddLocalUserFormState.Initial:
      case AddLocalUserFormState.NewUser:
        return false;

      case AddLocalUserFormState.OtherSite:
        return !this.getOtherSiteFlowModel().proceedClicked;

      case AddLocalUserFormState.NotAvailable:
        return true;
    }

    return false;
  }

  // section: create local user

  private createLocalSiteUser(username: string, model: INewUserFlowModel, authenticationProvider: IAuthenticationProvider) {
    // get the currently selected site role off the scope
    let siteRoleToGiveNewUser = this.$scope.siteRole;
    // set a flag to true if the currently selected role is Server Admin
    const makeServerAdmin = (siteRoleToGiveNewUser === SiteRoles.ServerAdministrator) || null;
    // if setting to server admin, we don't pass the role to the backend.
    // because the server admin flag & the site role string are mutually exclusive.
    if (makeServerAdmin) {
      siteRoleToGiveNewUser = null;
    }
    let action: ng.IPromise<IUserActionResult>;
    const isLocalAuth = authenticationProvider.type === AuthenticationProviderType.default && !this.serverService.isAuthenticationSaml();
    if (isLocalAuth) {
      action = this.SiteUsers.createLocalUser(
        username,
        model.displayName,
        model.email,
        model.password,
        makeServerAdmin,
        siteRoleToGiveNewUser
      );
    } else {
      action = this.SiteUsers.createLocalUserWithSamlOrSiteSamlAuth(
        username,
        model.displayName,
        model.email,
        authenticationProvider.id,
        makeServerAdmin,
        siteRoleToGiveNewUser
      );
    }
    action.then((result: any) => {
      this.showNotificationForCreateUser(result, siteRoleToGiveNewUser);
      if (!!result.errors && result.errors.length > 0) {
        this.reenableButtons();
      } else {
        this.$scope.$emit(UserAddedEvent);
        this.$scope.close(result);
      }
    });
  }

  private createLocalServerUser(username: string, model: INewUserFlowModel, authenticationProvider: IAuthenticationProvider) {
    let action: ng.IPromise<IUserActionResult>;
    const isLocalAuth = authenticationProvider.type === AuthenticationProviderType.default && !this.serverService.isAuthenticationSaml();
    if (isLocalAuth) {
      action = this.ServerUsers.createLocalServerUser(
      username,
      model.displayName,
      model.email,
      model.password);
    } else {
      action = this.ServerUsers.createLocalServerUserWithoutPassword(
        username,
        model.displayName,
        model.email
      );
    }
    action.then((result: IUserActionResult) => {
      if (!!result.errors && result.errors.length > 0) {
        this.reenableButtons();
      } else {
        this.onServerUserCreateSuccess(result);
      }
    });
  }

  // unit tests need to access this method
  public onServerUserCreateSuccess(result: IUserActionResult) {
    // create an interfaced object for the created user
    const addedUser: IUserInfo = {
      id: result.users[0].id,
      authId: result.users[0].authId,
      username: result.users[0].username,
      displayName: result.users[0].displayName
    };

    // update site membership
    if (!!this.$scope.siteRoleChanges.serverAdmin) {
      this.updateServerAdmin(addedUser);
    } else if (!_.isEmpty(this.$scope.siteRoleChanges.siteRoles)) {
      this.updateSiteMembership(addedUser.id, addedUser.authId, addedUser.username, addedUser.displayName);
    } else {
      this.showNotificationForCreateServerUser(addedUser, result);
    }
  }

  private showNotificationForCreateServerUser(user: IUserInfo, result: IUserActionResult){
    this.userActionNotification.notify({
      users: Set.fromIdItemArray(result.users),
      results: result.results,
      errors: result.errors,
      successCode: UserActionResultCode.systemUserCreated
    });
    this.$scope.$emit(SiteRolesEvent.SiteRolesChanged);
    this.$scope.close(user);
  }

  // section: update server admin

  private updateServerAdmin(user: IUserInfo) {
    const users = SetOfUsers.singletonForUser(user);
    const newAdminState = (this.$scope.siteRoleChanges.serverAdmin === CheckboxState.checked);
    this.rolesPickerClient.updateServerAdmin(users, newAdminState).then(() => {
      this.$scope.$emit(SiteRolesEvent.SiteRolesChanged);
      this.$scope.close(user);
    });
  }

  // section: update site membership or roles

  private updateSiteMembership(userId: string, authUserId: string, username: string, userDisplayName: string) {
    const params = this.rolesPickerClient.siteRoleChangesToParams([userId], this.$scope.siteRoleChanges.siteRoles);
    this.rolesPickerClient.updateServerUsersSiteMembership(params)
      .then((result: IUpdateServerUsersSiteMembershipResult) => {
        // create a Set<T> object for the result notification system
        const addedUser: IUserInfo = {
          id: userId,
          authId: authUserId,
          username: username,
          displayName: userDisplayName
        };
        const users = SetOfUsers.singletonForUser(addedUser);

        if(result.results){
          this.siteMembershipUpdateActionNotification.notify(
              result.results,
              users,
              this.$scope.siteRoleChanges.siteRoles);
        }
        this.$scope.$emit(SiteRolesEvent.SiteRolesChanged);
        this.$scope.close(addedUser);
      });
  }

  // section: add user to site

  private addUserToSite(username: string, model: IOtherSiteFlowModel) {
    let selectedSiteRole = this.$scope.siteRole;
    let promoteToServerAdmin = false;
    if (selectedSiteRole === SiteRoles.ServerAdministrator) {
      promoteToServerAdmin = true;
      // ServerAdmin isn't a real site role.
      // We will add the user with no site role and then promote them to Server Admin.
      selectedSiteRole = SiteRoles.Unlicensed;
    }

    this.SiteUsers.addUsersToSite([username], selectedSiteRole)
      .then((result: any) => {

        this.showNotificationForAddUserToSite(result, username, selectedSiteRole);

        if (!!result.errors && result.errors.length > 0) {
          this.reenableButtons();
        } else {
          const promoteAdminPromise = promoteToServerAdmin ? this.promoteToServerAdmin(username) : this.$q.when({});
          promoteAdminPromise.then((promoteAdminResult) => {
            if (!!promoteAdminResult.errors && promoteAdminResult.errors.length > 0) {
              this.reenableButtons();
            } else {
              this.$scope.$emit(UserAddedEvent);
              this.$scope.close(result);
            }
          });
        }
      });
  }

  private promoteToServerAdmin(username: string): ng.IPromise<any> {
    // TODO: if the backend can give us the IServerUser info (id is all we need) in the IUserNameAvailability result,
    //       we won't have to make this first request to get the Server User.
    let serverUser: IServerUser;
    return this.ServerUsers.getUserByUsernameAndDomain(username, 'local')
      .then((user: IServerUser) => {
        serverUser = user;
        return this.ServerUsers.updateServerAdminStatus([user.id], true);
      })
      .then((result: IResult) => {
        this.serverAdminUpdateActionNotification.notify(result, SetOfUsers.singletonForUser(serverUser), true);
        return result;
      });
  }

  // helpers

  private reenableButtons() {
    this.$scope.busy = false;
  }

  private getFormState() {
    return this.$scope.formModel.formState;
  }

  private showNotificationForCreateUser(result: IUserActionResult, siteRole: string) {
    const params: UserActionNotificationParams = {
      users: Set.fromIdItemArray(result.users),
      results: result.results,
      errors: result.errors,
      successCode: UserActionResultCode.siteUserCreated,
      siteRole: siteRole,
      showErrorMsgFunc: (errorMsg: string) => {
        this.$scope.formModel.errorText = errorMsg;
      }
    };
    this.userActionNotification.notify(params);
  }

  private showNotificationForAddUserToSite(result: IResult, username: string, siteRole: string) {
    this.addUserToSiteActionNotification.notify(
      (errorMsg: string) => {
        this.$scope.formModel.errorText = errorMsg;
      },
      result,
      username,
      siteRole);
  }

  // for UX FLOW #1 -- username is available

  private getNewUserFlowModel(): INewUserFlowModel {
    return this.$scope.formModel.newUserFlowModel;
  }

  // for UX FLOW #2 -- username exists on another site

  private getOtherSiteFlowModel(): IOtherSiteFlowModel {
    return this.$scope.formModel.otherSiteFlowModel;
  }

  // for UX FLOW #3 -- username exists on current site, thus not available

  private getNotAvailableFlowModel(): INotAvailableFlowModel {
    return this.$scope.formModel.notAvailableFlowModel;
  }
}

Module.instance().controller('AddLocalUserDialogCtrl', AddLocalUserDialogCtrl);
